/**
 * Top-level mobile gate.
 *
 * Mobile users (iOS or Android, including tablets) shouldn't transact through
 * this web flow — they belong in the native app. We try the community
 * deeplink first; if the OS doesn't hand the link to the installed app
 * within 1.5s we fall back to the appropriate store.
 *
 * Runs in `main.ts` before Vue mounts so it covers every route — OTP,
 * paywall, error, 404, debug. Desktop users are untouched.
 */
import { track } from "./telemetry";

const COMMUNITY_DEEPLINK_BASE = "ai.beconfident.app://community";
const EVENT_PATH_PATTERN = /^\/events\/([^/?#]+)\/?$/;

const APP_STORE_URL =
  import.meta.env.VITE_APP_STORE_URL ||
  "https://apps.apple.com/br/app/beconfident-aprenda-ingl%C3%AAs/id6468505840";
const PLAY_STORE_URL =
  import.meta.env.VITE_PLAY_STORE_URL ||
  "https://play.google.com/store/apps/details?id=ai.beconfident.app&hl=pt";
const WEBSITE_URL = import.meta.env.VITE_BECONFIDENT_URL || "https://beconfident.app/";

const STORE_FALLBACK_DELAY_MS = 1500;

export function isMobileUserAgent(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod|android/.test(navigator.userAgent.toLowerCase());
}

export function resolveStoreUrl(): string {
  if (typeof navigator === "undefined") return WEBSITE_URL;
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return APP_STORE_URL;
  if (/android/.test(ua)) return PLAY_STORE_URL;
  return WEBSITE_URL;
}

// Null when the visitor landed somewhere other than /events/:id.
export function extractEventId(pathname: string = window.location.pathname): string | null {
  return pathname.match(EVENT_PATH_PATTERN)?.[1] ?? null;
}

// Targets the specific event when the URL is /events/:id, otherwise lands the
// user on the community home so they can navigate from there.
export function resolveCommunityDeeplink(pathname: string = window.location.pathname): string {
  const eventId = extractEventId(pathname);
  return eventId
    ? `${COMMUNITY_DEEPLINK_BASE}/events/${encodeURIComponent(eventId)}`
    : COMMUNITY_DEEPLINK_BASE;
}

export function redirectMobileToApp(): void {
  const storeUrl = resolveStoreUrl();
  const deeplink = resolveCommunityDeeplink();
  track({
    type: "mobile_redirect",
    path: window.location.pathname + window.location.search,
    deeplink,
    storeFallback: storeUrl,
    eventId: extractEventId(),
  });

  const fallback = window.setTimeout(() => {
    window.location.href = storeUrl;
  }, STORE_FALLBACK_DELAY_MS);
  document.addEventListener(
    "visibilitychange",
    () => {
      if (document.hidden) window.clearTimeout(fallback);
    },
    { once: true },
  );
  window.location.href = deeplink;
}
